// FloatingPanel.js — Fenêtre flottante draggable avec resize et minimize

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { EventPanel } from './EventPanel';
import { CATEGORIES, formatYear } from '../data/events';
import { TutorialPanel } from './TutorialPanel';

// Compteur pour z-index des fenêtres (la dernière cliquée est au dessus)
let zCounter = 100;

function FloatingWindow({
  event, allEvents, onClose, onNavigateTo,
  user, onEdit, onReport,
  initialPos, initialSize,
  onFocus, minimizedIndex,
}) {
  const ref       = useRef(null);
  const dragRef   = useRef(false);
  const resizeRef = useRef(false);
  const offsetRef = useRef({ x: 0, y: 0 });
  const resizeStart = useRef({});

  const [pos,       setPos]       = useState(initialPos);
  const [size,      setSize]      = useState(initialSize || { w: 400, h: 580 });
  const [minimized, setMinimized] = useState(false);
  const [maximized, setMaximized] = useState(false);
  const [zIndex,    setZIndex]    = useState(++zCounter);

  const focus = useCallback(() => {
    zCounter++;
    setZIndex(zCounter);
    onFocus?.();
  }, [onFocus]);

  const clampPos = useCallback((x, y) => ({
    x: Math.max(0, Math.min(window.innerWidth  - size.w,  x)),
    y: Math.max(0, Math.min(window.innerHeight - 40, y)),
  }), [size.w]);

  // ── Drag ──────────────────────────────────────────────────────────────────
  const onDragStart = useCallback((e) => {
    if (e.target.closest('button') || e.target.closest('iframe') || e.target.closest('a')) return;
    e.preventDefault();
    focus();
    dragRef.current  = true;
    offsetRef.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
  }, [pos, focus]);

  // ── Resize ────────────────────────────────────────────────────────────────
  const onResizeStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    focus();
    resizeRef.current = true;
    resizeStart.current = { x: e.clientX, y: e.clientY, w: size.w, h: size.h };
  }, [size, focus]);

  useEffect(() => {
    const onMove = (e) => {
      if (dragRef.current) {
        const raw = { x: e.clientX - offsetRef.current.x, y: e.clientY - offsetRef.current.y };
        setPos(clampPos(raw.x, raw.y));
      }
      if (resizeRef.current) {
        const dx = e.clientX - resizeStart.current.x;
        const dy = e.clientY - resizeStart.current.y;
        setSize({
          w: Math.max(300, resizeStart.current.w + dx),
          h: Math.max(300, resizeStart.current.h + dy),
        });
      }
    };
    const onUp = () => { dragRef.current = false; resizeRef.current = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [clampPos]);

  const cat = CATEGORIES[event.category];

  // Position minimized : barre en bas à gauche, empilées
  const minimizedBottom = 165; // au dessus de la timeline
  
  const winStyle = maximized ? {
    position: 'fixed', left: 0, top: 0,
    width: '100vw', height: '100vh',
    zIndex, borderRadius: 0,
  } : minimized ? {
    position: 'fixed',
    left: 20 + (minimizedIndex || 0) * 274,
    bottom: minimizedBottom,
    top: 'auto',
    width: 260,
    height: 38,
    zIndex,
    borderRadius: 8,
    opacity: 0.92,
    transition: 'left 0.2s ease',
  } : {
    position: 'fixed',
    left: pos.x, top: pos.y,
    width:  size.w,
    height: size.h,
    zIndex,
    borderRadius: 12,
  };

  return (
    <div
      ref={ref}
      className="float-window"
      style={winStyle}
      onMouseDown={focus}
    >
      {/* ── Barre de titre ── */}
      <div
        className="float-titlebar drag-handle"
        onMouseDown={onDragStart}
        style={{ borderBottom: `2px solid ${cat.color}` }}
      >
        {/* Icône + titre */}
        <div className="float-title-left">
          <span className="float-cat-icon">{cat.icon}</span>
          <div className="float-title-text">
            <span className="float-title">{event.title}</span>
            <span className="float-year" style={{ color: cat.color }}>
              {formatYear(event.year)}
            </span>
          </div>
        </div>

        {/* Boutons fenêtre style macOS */}
        <div className="float-window-btns" onMouseDown={e => e.stopPropagation()}>
          <button
            className="float-btn float-btn-min"
            onClick={() => {
              if (!minimized) {
                // Mémorise la position avant minimize
                setPos(p => ({ ...p }));
              }
              setMinimized(m => !m);
            }}
            title={minimized ? 'Restaurer' : 'Réduire'}
          />
          <button
            className="float-btn float-btn-max"
            onClick={() => { setMaximized(m => !m); setMinimized(false); }}
            title={maximized ? 'Restaurer' : 'Agrandir'}
          />
          <button
            className="float-btn float-btn-close"
            onClick={onClose}
            title="Fermer"
          />
        </div>
      </div>

      {/* ── Contenu ── */}
      {!minimized && (
        <div className="float-content">
          <EventPanel
            event={event}
            allEvents={allEvents}
            onClose={onClose}
            onNavigateTo={onNavigateTo}
            user={user}
            onEdit={onEdit}
            onReport={onReport}
            hideHeader
          />
        </div>
      )}

      {/* ── Poignée resize ── */}
      {!minimized && !maximized && (
        <div className="float-resize-handle" onMouseDown={onResizeStart} />
      )}
    </div>
  );
}


// ── Fenêtre tutoriel flottante ────────────────────────────────────────────────
export function TutorialWindow({ onClose }) {
  const ref       = useRef(null);
  const dragRef   = useRef(false);
  const offsetRef = useRef({ x: 0, y: 0 });

  const initX = window.innerWidth - Math.round(window.innerWidth / 2) - 20;
  const initW = Math.round(window.innerWidth / 2) - 30;
  const initH = window.innerHeight - 240;

  const [pos,       setPos]       = useState({ x: initX, y: 70 });
  const [minimized, setMinimized] = useState(false);
  const [zIndex,    setZIndex]    = useState(++zCounter);

  const clampPos = (x, y) => ({
    x: Math.max(0, Math.min(window.innerWidth - initW, x)),
    y: Math.max(0, Math.min(window.innerHeight - 40, y)),
  });

  const onDragStart = (e) => {
    if (e.target.closest('button')) return;
    e.preventDefault();
    zCounter++; setZIndex(zCounter);
    dragRef.current = true;
    offsetRef.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current) return;
      setPos(clampPos(e.clientX - offsetRef.current.x, e.clientY - offsetRef.current.y));
    };
    const onUp = () => { dragRef.current = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [pos]);

  const winStyle = minimized ? {
    position: 'fixed', left: pos.x, bottom: 165, top: 'auto',
    width: 260, height: 38, zIndex, borderRadius: 8,
  } : {
    position: 'fixed', left: pos.x, top: pos.y,
    width: initW, height: initH, zIndex, borderRadius: 12,
  };

  return (
    <div ref={ref} className="float-window" style={winStyle} onMouseDown={() => { zCounter++; setZIndex(zCounter); }}>
      {/* Titlebar */}
      <div className="float-titlebar" onMouseDown={onDragStart} style={{ borderBottom: '2px solid #4488ff' }}>
        <div className="float-title-left">
          <span className="float-cat-icon">🧭</span>
          <div className="float-title-text">
            <span className="float-title">Guide de démarrage</span>
            <span className="float-year" style={{ color: '#4488ff' }}>Kronotera</span>
          </div>
        </div>
        <div className="float-window-btns" onMouseDown={e => e.stopPropagation()}>
          <button className="float-btn float-btn-min" onClick={() => setMinimized(m => !m)} title={minimized ? 'Restaurer' : 'Réduire'} />
          <button className="float-btn float-btn-close" onClick={onClose} title="Fermer" />
        </div>
      </div>

      {/* Contenu */}
      {!minimized && (
        <div className="float-content">
          <TutorialPanel onClose={onClose} inPanel />
        </div>
      )}
    </div>
  );
}

// ── Gestionnaire de fenêtres flottantes ───────────────────────────────────────
export function FloatingPanelManager({
  openPanels, onClose, onNavigateTo,
  allEvents, user, onEdit, onReport,
}) {
  // Cascade : chaque nouvelle fenêtre décalée de 30px
  return (
    <>
      {openPanels.map((panel, i) => (
        <FloatingWindow
          key={panel.id}
          event={panel.event}
          allEvents={allEvents}
          onClose={() => onClose(panel.id)}
          onNavigateTo={onNavigateTo}
          user={user}
          onEdit={onEdit}
          onReport={onReport}
          initialPos={panel.pos}
          initialSize={panel.size}
          minimizedIndex={i}
        />
      ))}
    </>
  );
}
